import React, { useState, useEffect } from 'react';
import { PaperData, Flashcard } from '../types/paper';
import { MathView } from './MathView';
import { 
  Swords, ShieldAlert, Heart, Zap, Award, RotateCcw, 
  Sparkles, Flame, CheckCircle2, XCircle, Skull, Trophy
} from 'lucide-react';

interface MonsterBattleProps {
  paper: PaperData;
}

interface Boss {
  name: string;
  title: string;
  maxHp: number;
  currentHp: number;
  avatar: string;
  taunts: string[];
}

export const MonsterBattle: React.FC<MonsterBattleProps> = ({ paper }) => {
  const cards = paper.flashcards;
  const [cardIndex, setCardIndex] = useState<number>(0);
  const [playerHp, setPlayerHp] = useState<number>(100);
  const [maxPlayerHp] = useState<number>(100);
  const [isFlipped, setIsFlipped] = useState<boolean>(false);
  const [battleLog, setBattleLog] = useState<string[]>([]);
  const [animState, setDynamicAnimState] = useState<'idle' | 'player_attack' | 'boss_attack' | 'victory' | 'defeat'>('idle');
  const [shake, setShake] = useState<boolean>(false);

  const currentCard = cards[cardIndex] || cards[0];

  // Dynamic Boss based on topic/paper
  const [boss, setBoss] = useState<Boss>({
    name: 'The Entropy Kraken',
    title: 'Guardian of Forgotten Knowledge',
    maxHp: cards.length * 25,
    currentHp: cards.length * 25,
    avatar: '🦑',
    taunts: [
      'Your mathematical derivations are weak!',
      'Can you explain the variance scaling factor before I crush your HP?',
      'Forgetfulness is inevitable! Taste my entropy attack!',
      'Only true mastery can bypass my gradient barrier!'
    ]
  });

  const [currentTaunt, setCurrentTaunt] = useState<string>(boss.taunts[0]);

  // Reset Battle
  const handleResetBattle = () => {
    setPlayerHp(100);
    setBoss(prev => ({ ...prev, currentHp: cards.length * 25 }));
    setCardIndex(0);
    setIsFlipped(false);
    setBattleLog(['⚔️ The battle begins! Answer flashcard questions correctly to strike the boss!']);
    setDynamicAnimState('idle');
  };

  const triggerShake = () => {
    setShake(true);
    setTimeout(() => setShake(false), 500);
  };

  const handleUserAnswer = (isCorrect: boolean) => {
    if (!currentCard) return;

    if (isCorrect) {
      // Player strikes boss
      const damage = 25;
      const newBossHp = Math.max(0, boss.currentHp - damage);
      setBoss(prev => ({ ...prev, currentHp: newBossHp }));
      setDynamicAnimState('player_attack');
      triggerShake();

      const logMsg = `💥 CRITICAL STRIKE! You answered correctly and dealt ${damage} DAMAGE to ${boss.name}!`;
      setBattleLog(prev => [logMsg, ...prev]);

      if (newBossHp === 0) {
        setDynamicAnimState('victory');
        setBattleLog(prev => [`🏆 VICTORY! You defeated ${boss.name} and conquered this topic!`, ...prev]);
        return;
      }
    } else {
      // Boss attacks player
      const damage = 20;
      const newPlayerHp = Math.max(0, playerHp - damage);
      setPlayerHp(newPlayerHp);
      setDynamicAnimState('boss_attack');
      triggerShake();

      const logMsg = `🔥 BOSS COUNTER-ATTACK! ${boss.name} dealt ${damage} DAMAGE to your HP!`;
      setBattleLog(prev => [logMsg, ...prev]);

      if (newPlayerHp === 0) {
        setDynamicAnimState('defeat');
        setBattleLog(prev => [`💀 DEFEAT! ${boss.name} overwhelmed your knowledge defense. Review the flashcards and retry!`, ...prev]);
        return;
      }
    }

    // Select new boss taunt
    const randomTaunt = boss.taunts[Math.floor(Math.random() * boss.taunts.length)];
    setCurrentTaunt(randomTaunt);

    // Advance to next card
    setTimeout(() => {
      setDynamicAnimState('idle');
      setIsFlipped(false);
      if (cardIndex < cards.length - 1) {
        setCardIndex(prev => prev + 1);
      } else {
        setCardIndex(0);
      }
    }, 1200);
  };

  const bossHpPercentage = Math.round((boss.currentHp / boss.maxHp) * 100);
  const playerHpPercentage = Math.round((playerHp / maxPlayerHp) * 100);

  return (
    <div className={`glass-panel rounded-2xl p-6 border border-slate-800 shadow-2xl relative overflow-hidden transition-transform ${shake ? 'translate-x-1 -translate-y-1' : ''}`}>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 pb-4 border-b border-slate-800">
        <div>
          <div className="flex items-center gap-2 text-rose-400 text-sm font-semibold uppercase tracking-wider">
            <Swords className="w-4 h-4" /> Gamified Knowledge Arena
          </div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">
            Monster Flashcard Battle
          </h2>
        </div>

        <button
          onClick={handleResetBattle}
          className="flex items-center gap-1.5 px-3.5 py-2 bg-slate-800 hover:bg-slate-700 text-slate-200 border border-slate-700 rounded-xl text-xs font-semibold transition-all"
        >
          <RotateCcw className="w-3.5 h-3.5" /> Restart Battle
        </button>
      </div>

      {/* Battle Arena Stage */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-6">
        
        {/* Player Status Card */}
        <div className="lg:col-span-4 bg-dark-card/90 border border-slate-800 rounded-2xl p-5 space-y-4 shadow-xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-full bg-brand-600/30 border border-brand-500 flex items-center justify-center text-xl">
                🧙‍♂️
              </div>
              <div>
                <div className="font-bold text-white text-sm">Scholar Knight</div>
                <div className="text-[11px] text-brand-300 font-mono">Level 12 Researcher</div>
              </div>
            </div>
            <div className="text-right">
              <div className="text-xs font-bold text-rose-400 flex items-center gap-1 justify-end">
                <Heart className="w-3.5 h-3.5 fill-rose-500 text-rose-500" /> {playerHp} / {maxPlayerHp} HP
              </div>
            </div>
          </div>

          {/* Player HP Bar */}
          <div className="w-full h-3 bg-slate-950 rounded-full overflow-hidden border border-slate-800 p-0.5">
            <div 
              className="h-full bg-gradient-to-r from-rose-600 to-emerald-500 rounded-full transition-all duration-500"
              style={{ width: `${playerHpPercentage}%` }}
            />
          </div>

          {/* Battle Log */}
          <div className="p-3 bg-dark-bg/80 rounded-xl border border-slate-800 h-40 overflow-y-auto font-mono text-[11px] space-y-1.5 text-slate-300">
            <div className="text-slate-500 font-semibold border-b border-slate-800 pb-1">BATTLE LOG:</div>
            {battleLog.map((log, i) => (
              <div key={i} className="leading-tight">{log}</div>
            ))}
          </div>
        </div>

        {/* Boss Monster Stage */}
        <div className="lg:col-span-8 bg-dark-card/90 border border-rose-900/40 rounded-2xl p-6 relative flex flex-col justify-between overflow-hidden shadow-2xl">
          <div className="absolute top-0 right-0 w-64 h-64 bg-rose-500/10 rounded-full blur-3xl pointer-events-none" />

          {/* Boss Header & Health */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <div>
                <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-rose-950 text-rose-300 border border-rose-500/40">
                  BOSS MONSTER
                </span>
                <h3 className="text-xl font-extrabold text-white mt-1">{boss.name}</h3>
                <div className="text-xs text-rose-300">{boss.title}</div>
              </div>
              <div className="text-right font-mono text-xs font-bold text-rose-400">
                {boss.currentHp} / {boss.maxHp} HP ({bossHpPercentage}%)
              </div>
            </div>

            {/* Boss HP Bar */}
            <div className="w-full h-4 bg-slate-950 rounded-full overflow-hidden border border-slate-800 p-0.5">
              <div 
                className="h-full bg-gradient-to-r from-rose-600 via-amber-500 to-rose-500 rounded-full transition-all duration-500 shadow-glow-rose"
                style={{ width: `${bossHpPercentage}%` }}
              />
            </div>
          </div>

          {/* Boss Avatar & Taunt Dialogue */}
          <div className="my-6 flex flex-col sm:flex-row items-center gap-6 justify-center">
            <div className={`text-7xl transition-transform duration-300 ${
              animState === 'boss_attack' ? 'scale-125 rotate-12 text-rose-500' :
              animState === 'player_attack' ? 'opacity-50 blur-xs scale-90' : 'animate-bounce'
            }`}>
              {boss.avatar}
            </div>

            {/* Speech Bubble */}
            <div className="relative bg-slate-900/90 border border-rose-500/30 p-4 rounded-2xl max-w-md text-xs text-rose-200 font-mono shadow-xl">
              <div className="font-bold text-rose-400 mb-1">💬 {boss.name} speaks:</div>
              "{currentTaunt}"
            </div>
          </div>

        </div>

      </div>

      {/* Flashcard Question / Answer Battle Turn */}
      {currentCard && animState !== 'victory' && animState !== 'defeat' && (
        <div className="glass-card p-6 rounded-2xl border border-slate-700/80 space-y-4">
          <div className="flex items-center justify-between text-xs text-slate-400">
            <span className="font-mono font-bold text-brand-400">
              CARD {cardIndex + 1} OF {cards.length}
            </span>
            <span className="px-2.5 py-0.5 rounded-full bg-slate-800 text-slate-300 font-semibold uppercase">
              {currentCard.difficulty || 'medium'}
            </span>
          </div>

          {/* Prompt Question */}
          <h3 className="text-lg font-bold text-white leading-relaxed">
            {currentCard.question}
          </h3>

          {!isFlipped ? (
            <button
              onClick={() => setIsFlipped(true)}
              className="w-full py-3 bg-brand-600 hover:bg-brand-500 text-white rounded-xl text-xs font-bold shadow-glow-blue transition-all flex items-center justify-center gap-2"
            >
              <Sparkles className="w-4 h-4" /> Reveal Answer & Verify Recall
            </button>
          ) : (
            <div className="space-y-4 animate-fade-in pt-3 border-t border-slate-800">
              <div className="p-4 bg-dark-bg/90 rounded-xl border border-slate-800 text-sm text-slate-200 leading-relaxed whitespace-pre-line">
                <div className="text-xs font-semibold text-emerald-400 mb-2">Answer Breakdown:</div>
                {currentCard.answer}

                {currentCard.formula && (
                  <div className="my-3 p-3 bg-slate-950 rounded-lg text-center overflow-x-auto">
                    <MathView math={currentCard.formula} block={true} className="text-brand-300" />
                  </div>
                )}
              </div>

              {/* Assessment Action Buttons */}
              <div className="flex flex-col sm:flex-row items-center justify-between gap-3 p-3 bg-dark-bg/80 rounded-xl border border-slate-800">
                <div className="text-xs font-bold text-slate-300">Did you recall this answer correctly?</div>
                <div className="flex items-center gap-2 w-full sm:w-auto">
                  <button
                    onClick={() => handleUserAnswer(false)}
                    className="flex-1 sm:flex-none flex items-center justify-center gap-1.5 px-5 py-2.5 bg-rose-950 hover:bg-rose-900 border border-rose-500/40 text-rose-200 rounded-xl text-xs font-bold transition-all"
                  >
                    <XCircle className="w-4 h-4 text-rose-400" /> Incorrect (Take Damage)
                  </button>
                  <button
                    onClick={() => handleUserAnswer(true)}
                    className="flex-1 sm:flex-none flex items-center justify-center gap-1.5 px-5 py-2.5 bg-emerald-950 hover:bg-emerald-900 border border-emerald-500/40 text-emerald-200 rounded-xl text-xs font-bold transition-all shadow-glow-emerald"
                  >
                    <CheckCircle2 className="w-4 h-4 text-emerald-400" /> Correct (Attack Boss!)
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* VICTORY OVERLAY */}
      {animState === 'victory' && (
        <div className="p-8 bg-emerald-950/80 border border-emerald-500/50 rounded-2xl text-center space-y-4 animate-fade-in">
          <Trophy className="w-16 h-16 text-amber-400 mx-auto animate-bounce" />
          <h3 className="text-3xl font-extrabold text-white">CONQUERED THE MONSTER!</h3>
          <p className="text-sm text-emerald-200 max-w-md mx-auto leading-relaxed">
            You successfully defeated <strong>{boss.name}</strong> by mastering the flashcard concepts! Your knowledge barrier remains unbreakable.
          </p>
          <button
            onClick={handleResetBattle}
            className="px-6 py-3 bg-emerald-600 hover:bg-emerald-500 text-white rounded-xl text-xs font-bold shadow-glow-emerald transition-all"
          >
            Play Battle Again
          </button>
        </div>
      )}

      {/* DEFEAT OVERLAY */}
      {animState === 'defeat' && (
        <div className="p-8 bg-rose-950/80 border border-rose-500/50 rounded-2xl text-center space-y-4 animate-fade-in">
          <Skull className="w-16 h-16 text-rose-400 mx-auto" />
          <h3 className="text-3xl font-extrabold text-white">DEFEATED IN BATTLE</h3>
          <p className="text-sm text-rose-200 max-w-md mx-auto leading-relaxed">
            The entropy beast drained your health. Review the concept wiki and flashcards to build your mastery and try again!
          </p>
          <button
            onClick={handleResetBattle}
            className="px-6 py-3 bg-rose-600 hover:bg-rose-500 text-white rounded-xl text-xs font-bold shadow-glow-rose transition-all"
          >
            Retry Battle
          </button>
        </div>
      )}

    </div>
  );
};
